/**
 * Cloudflare Images integration for Shopify
 * Version: 2.0.2 - Updated 2025-10-19 19:50
 * 
 * This script provides functionality to:
 * 1. Upload images to Cloudflare Images API
 * 2. Transform existing image URLs to responsive Cloudflare URLs
 * 3. Apply responsive srcset and sizes attributes to images
 */

const CloudflareImages = (function() {
  // Configuration - Updated with current credentials
  const config = {
    accountId: '02d509343cda1b39d792e11cf60b4089',
    accountHash: 'kqtAfraFTcgD333DFm79cw',
    zoneId: '5ecb35fcebc0a5332b3bb2c8a89eb80f',
    deliveryUrl: 'https://imagedelivery.net/kqtAfraFTcgD333DFm79cw',
    apiToken: '', // API token should be set server-side only
    variants: {
      thumbnail: { width: 320 },
      small: { width: 640 },
      medium: { width: 960 },
      large: { width: 1280 },
      xlarge: { width: 1920 }
    },
    defaultSizes: '(min-width: 1150px) 20vw, (min-width: 768px) 33vw, 100vw'
  };

  /**
   * Upload an image to Cloudflare Images
   * @param {File|Blob} file - The image file to upload
   * @returns {Promise<Object>} - Response with image ID and other info
   */
  async function uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(
        `https://api.cloudflare.com/client/v4/accounts/${config.accountId}/images/v1`, 
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${config.apiToken}`
          },
          body: formData
        }
      );

      const data = await response.json();
      
      if (!data.success) {
        throw new Error('Upload failed: ' + (data.errors?.[0]?.message || 'Unknown error'));
      }
      
      return data.result;
    } catch (error) {
      console.error('Cloudflare image upload error:', error);
      throw error;
    }
  }

  /**
   * Generate a responsive image URL with srcset
   * @param {string} imageId - Cloudflare image ID
   * @param {string} sizes - Sizes attribute for the image
   * @returns {Object} - Object with src, srcset and sizes
   */
  function getResponsiveImageUrl(imageId, sizes = config.defaultSizes) {
    // Default src is medium variant
    const src = `${config.deliveryUrl}/${imageId}/medium`;
    
    // Generate srcset with all variants
    const srcset = Object.entries(config.variants)
      .map(([variant, dims]) => `${config.deliveryUrl}/${imageId}/${variant} ${dims.width}w`)
      .join(', ');
    
    return {
      src,
      srcset,
      sizes
    };
  }

  /**
   * Get Cloudflare ID from Shopify image
   */
  function getCloudflareId(img) {
    // Check if image has data-shopify-id attribute
    const shopifyId = img.getAttribute('data-shopify-id');
    if (shopifyId) {
      return `shopify-product-${shopifyId}`;
    }
    
    // Fallback: try to extract from src
    const src = img.src || img.getAttribute('src');
    if (!src) return null;
    
    // If already a Cloudflare image
    if (src.includes('imagedelivery.net')) {
      const parts = src.split('/');
      return parts[parts.length - 2]; // Get ID from URL
    }
    
    // Extract from Shopify URL - preserve full filename including UUID
    if (src.includes('cdn.shopify.com') || src.includes('/cdn/shop/')) {
      const urlParts = src.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0];
      
      // Remove file extension but KEEP the UUID
      const cleanFilename = filename.replace(/\.[^/.]+$/, '');
      
      // Return full filename with UUID to match Cloudflare IDs
      return `shopify-${cleanFilename}`;
    }
    
    return null;
  }

  /**
   * Convert Shopify image to Cloudflare delivery
   */
  function convertToCloudflare(img) {
    if (img.hasAttribute('data-cf-processed')) return;
    
    const cfId = getCloudflareId(img);
    if (!cfId) return;
    
    const originalSrc = img.src;
    const sizes = img.getAttribute('sizes') || config.defaultSizes;
    
    // Set Cloudflare URLs using Cloudflare's built-in variants
    img.src = `${config.deliveryUrl}/${cfId}/n`; // 'n' = normal/medium
    
    // Generate srcset with Cloudflare's built-in variants
    const srcset = [
      `${config.deliveryUrl}/${cfId}/xs 480w`,   // xs = 480px
      `${config.deliveryUrl}/${cfId}/s 768w`,    // s = 768px
      `${config.deliveryUrl}/${cfId}/n 1024w`,   // n = 1024px
      `${config.deliveryUrl}/${cfId}/l 1150w`,   // l = 1150px
      `${config.deliveryUrl}/${cfId}/xl 2560w`   // xl = 2560px
    ].join(', ');
    
    img.srcset = srcset;
    img.sizes = sizes;
    img.setAttribute('data-cf-image', 'true');
    img.setAttribute('data-cf-id', cfId);
    img.setAttribute('data-cf-processed', 'true');
    
    // Fallback to original if Cloudflare image doesn't exist
    img.onerror = function() {
      this.onerror = null;
      console.log('[Cloudflare Images] Image not found with ID: ' + cfId + ' - Falling back to Shopify CDN');
      this.src = originalSrc;
      this.srcset = '';
      this.removeAttribute('data-cf-image');
    };
  }

  /**
   * Make Shopify images responsive with Cloudflare delivery
   */
  function makeImagesResponsive() {
    const images = document.querySelectorAll('img[src*="cdn.shopify.com"], img[src*="/cdn/shop/"], img[data-shopify-id]');
    
    images.forEach(img => {
      convertToCloudflare(img);
    });
  }
  
  /**
   * Legacy responsive function (kept for compatibility)
   */
  function makeImagesResponsiveLegacy() {
    const images = document.querySelectorAll('img[src*="cdn.shopify.com"]');
    
    images.forEach(img => {
      // Skip already processed images
      if (img.dataset.cfProcessed) return;

      // Detect image aspect ratio for proper sizing
      const aspectRatio = img.height && img.width ? img.width / img.height : 1;
      
      // Default size based on container context
      let sizes = config.defaultSizes;
      
      // Adjust sizes based on image context
      if (img.closest('.hdt-collection-img')) {
        sizes = '(min-width: 1150px) calc(20% - 24px), (min-width: 768px) calc(33.333% - 20px), calc(50% - 16px)';
      } else if (img.closest('.product__media')) {
        sizes = '(min-width: 768px) 50vw, 100vw';
      }
      
      // Apply srcset for native Shopify images, using their CDN capabilities
      if (img.src.includes('cdn.shopify.com')) {
        const baseUrl = img.src.split('?')[0];
        const srcset = [
          `${baseUrl}?width=320 320w`,
          `${baseUrl}?width=640 640w`,
          `${baseUrl}?width=960 960w`,
          `${baseUrl}?width=1280 1280w`,
          `${baseUrl}?width=1920 1920w`
        ].join(', ');
        
        img.srcset = srcset;
        img.sizes = sizes;
        img.dataset.cfProcessed = 'shopify';
      }
    });
  }

  // Debug logging
  console.log('[Cloudflare Images] Script loaded');
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      makeImagesResponsive();
      
      // Re-run when content might have changed (important for infinite scroll or AJAX)
      const observer = new MutationObserver(mutations => {
        makeImagesResponsive();
      });
      
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
    });
  } else {
    makeImagesResponsive();
    
    // Re-run when content might have changed (important for infinite scroll or AJAX)
    const observer = new MutationObserver(mutations => {
      makeImagesResponsive();
    });
    
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }
  
  // Log first image for debugging
  setTimeout(() => {
    const firstImg = document.querySelector('img[src*="cdn.shopify.com"], img[src*="/cdn/shop/"]');
    if (firstImg) {
      console.log('[Cloudflare Images] Sample image:', {
        src: firstImg.src,
        extractedId: getCloudflareId(firstImg),
        hasDataId: firstImg.getAttribute('data-shopify-id'),
        processed: firstImg.getAttribute('data-cf-processed'),
        cfImage: firstImg.getAttribute('data-cf-image')
      });
    }
    
    // Count how many images were processed
    const allShopifyImages = document.querySelectorAll('img[src*="cdn.shopify.com"], img[src*="/cdn/shop/"]');
    const processedImages = document.querySelectorAll('img[data-cf-processed="true"]');
    console.log(`[Cloudflare Images] Processed ${processedImages.length} of ${allShopifyImages.length} Shopify images`);
  }, 2000);

  // Public API
  return {
    uploadImage,
    getResponsiveImageUrl,
    makeImagesResponsive
  };
})();
